import { Suspense } from "react";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { cn } from "@/frontend/lib/utils";
import { useOrgSettingsStore } from "@/frontend/stores/orgSettingsStore";
import { Skeleton } from "@/frontend/components/ui/skeleton";
import { TourHelpButton } from "@/frontend/components/ProductTour";

function SalesLoadingState() {
  return (
    <div className="h-full overflow-hidden bg-slate-50 p-4 dark:bg-background" role="status" aria-label="Loading sales workspace">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-4 w-52" />
        </div>
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((column) => (
          <div key={column} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-border dark:bg-card">
            <div className="mb-3 flex items-center justify-between">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-5 w-7 rounded-full" />
            </div>
            <div className="space-y-2.5">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SalesLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const { salesDealsEnabled, salesCompaniesEnabled } = useOrgSettingsStore();
  const salesNavigation = [
    { label: "Leads", path: "leads" },
    ...(salesDealsEnabled ? [{ label: "Deals", path: "pipeline" }] : []),
    ...(salesCompaniesEnabled ? [{ label: "Companies", path: "accounts" }] : []),
    { label: "Ads", path: "ads" },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-background">
      <header className="shrink-0 border-b border-slate-200 bg-white dark:border-border dark:bg-card">
        <div className="flex h-12 items-stretch gap-5 px-3 sm:px-4">
          <div className="hidden items-center border-r border-slate-200 pr-5 sm:flex dark:border-border">
            <p className="text-sm font-semibold text-slate-950 dark:text-foreground">Sales</p>
          </div>
          <nav aria-label="Sales" data-tour="sales-nav" className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex h-full min-w-max items-stretch gap-5">
              {salesNavigation.map((item) => (
                <NavLink
                  key={item.path}
                  to={`/org/${orgId}/app/sales/${item.path}`}
                  className={({ isActive }) => cn(
                    "flex h-full items-center border-b-2 px-0.5 text-sm transition-colors",
                    isActive
                      ? "border-emerald-700 font-medium text-slate-950 dark:border-emerald-400 dark:text-foreground"
                      : "border-transparent text-slate-500 hover:text-slate-950 dark:text-muted-foreground dark:hover:text-foreground",
                  )}
                >
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>
          <TourHelpButton tourId="sales" className="self-center" />
        </div>
      </header>
      <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <Suspense fallback={<SalesLoadingState />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
